'use client'

import Link from 'next/link'
import React from 'react'
import type { Locale } from '@/i18n'

import type { Footer } from '@/payload-types'

import { CMSLink } from '@/components/Link'
import { SocialLinks } from '@/components/SocialLinks'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import type { EnvironmentConfig } from '@/utilities/getEnvironment'
import { getFooterTranslations } from './translations'
import { trackContactEmailClick, trackContactPhoneClick } from '@/utilities/analytics'

interface FooterClientProps {
  data: Footer
  locale?: Locale
  envConfig?: EnvironmentConfig
}

export function FooterClient({ data, locale, envConfig }: FooterClientProps) {
  const navItems = data?.navItems || []
  const contactInfo = data?.contactInfo
  const currentYear = new Date().getFullYear()
  const t = getFooterTranslations(locale || 'uk')
  const homeUrl = `/${locale || 'uk'}`

  // Use admin-configured description if available, otherwise fall back to translations
  const descriptionLine1 = data?.description?.line1 || t.description.line1
  const descriptionLine2 = data?.description?.line2 || t.description.line2

  // Use environment config passed from server
  const logoColor = envConfig?.showBadge ? envConfig.color : undefined

  return (
    <footer className="mt-auto bg-background border-t border-border">
      {/* Main Footer Content */}
      <div className="container py-8">
        <div className="grid grid-cols-2 gap-6 md:gap-8 md:[grid-template-columns:1fr_1fr_1fr_1fr_1fr_minmax(120px,1fr)_1fr_1fr]">
          {/* Logo and Description - full width on mobile, 3 columns on desktop */}
          <div className="col-span-2 md:col-span-3 flex flex-row md:flex-col items-center md:items-start gap-4 mb-2 md:mb-0">
            <Link
              className="flex items-center text-4xl font-bold"
              href={homeUrl}
              style={logoColor ? { color: logoColor } : {}}
            >
              RS-42
            </Link>
            <p className="text-sm text-muted-foreground">
              {descriptionLine1}
              <br />
              {descriptionLine2}
            </p>
          </div>

          {/* Empty space - spans 1 column (desktop only) */}
          <div className="hidden md:block md:col-span-1"></div>

          {/* Navigation - 1 column in 2x2 grid on mobile, 1 column on desktop */}
          <div className="flex flex-col gap-3 md:col-span-1">
            <h3 className="font-semibold text-sm">{t.navigation}</h3>
            <nav className="flex flex-col gap-2 items-start" aria-label="Footer navigation">
              {navItems.map(({ link }, i) => {
                return (
                  <CMSLink
                    key={i}
                    {...link}
                    appearance="link"
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  />
                )
              })}
            </nav>
          </div>

          {/* Contact Information - 1 column in 2x2 grid on mobile, 1 column on desktop */}
          {contactInfo && (contactInfo.email || contactInfo.phone || contactInfo.address) && (
            <div className="flex flex-col gap-3 md:col-span-1">
              <h3 className="font-semibold text-sm">{t.contact}</h3>
              <div className="flex flex-col gap-2 text-sm">
                {contactInfo.email && (
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-muted-foreground hover:text-foreground transition-colors break-all"
                    onClick={() => trackContactEmailClick(contactInfo.email!)}
                  >
                    {contactInfo.email}
                  </a>
                )}
                {contactInfo.phone && (
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => trackContactPhoneClick(contactInfo.phone!)}
                  >
                    {contactInfo.phone}
                  </a>
                )}
                {contactInfo.address && (
                  <p className="text-muted-foreground">{contactInfo.address}</p>
                )}
              </div>
            </div>
          )}

          {/* Social Media - 1 column in 2x2 grid on mobile, 1 column on desktop */}
          <div className="flex flex-col gap-3 md:col-span-1">
            <SocialLinks locale={locale} socialLinks={data?.socialLinks} />
          </div>

          {/* Language Switcher - 1 column in 2x2 grid on mobile, 1 column on desktop */}
          <div className="flex flex-col gap-3 md:col-span-1">
            <h3 className="font-semibold text-sm">{t.language}</h3>
            <LanguageSwitcher currentLocale={locale || 'uk'} />
          </div>
        </div>
      </div>

      {/* Copyright and Developer Info */}
      <div className="border-t border-border pb-24 md:pb-0">
        <div className="container py-4 flex flex-col md:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
          <div>
            © {currentYear} RS-42. {t.copyright}
          </div>
          <div className="flex items-center gap-2">
            <span>{t.developer}</span>
            <a
              href="https://t.me/mag1ck_drak0n"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground transition-colors"
            >
              Іван Побережнюк (@mag1ck_drak0n)
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
